import { Box, Button, Divider, Fade, Grid, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { useGlobals } from 'src/shared/contexts/appContext';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { EmbededCodePreviewModal } from 'src/shared/components/organisms/embededCodePreviewModal';
import { useWebFormArrays } from 'src/shared/hooks/useWebFormArrays';
import { FundraiserSettingsAutocomplete } from 'src/modules/fundraiser/components/fundraiserSettingsAutocomplete';

type WebFormEmbedCodeProps = {
  webFormName: string;
};

export const WebFormEmbedCode = (props: WebFormEmbedCodeProps) => {
  const { webFormName } = props;
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const { themeTemplateArray, langTemplateArray } = useWebFormArrays();

  const [displayCopyMessage, setDisplayCopyMessage] = useState<boolean>(false);
  const [template, setTemplate] = useState<SelectableItem>(themeTemplateArray[0]);
  const [language, setLanguage] = useState<SelectableItem>(langTemplateArray[0]);
  const [policyUrl, setPolicyUrl] = useState<string>(''); // För alla typer - Göra required?
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);

  const getTemplate = () => {
    // Högtidsgåva har ingen inbäddnignskod än

    return `<!--- CODE TO EMBED --->
        <script src="https://forms.foreningssupport.se/js/embed/membersignup.js"></script>
        <div id="fs-signup-member-widget"></div>
        <script>
        MemberSignUpFormComponent({cId: "${currentUser?.custName}",  
        webForm: '${webFormName}', 
        policyUrl: '${policyUrl}', 
        lang: '${language.value}',
        theme: '${template.value}', navigateScroll: function(){ var element = document.getElementById('fs-signup-member-widget');element.scrollIntoView()}, resizeMe: function (size) {var element = document.getElementById('fs-signup-member-widget');element.firstElementChild.setAttribute("style","height:" + size + "px");}}).render('#fs-signup-member-widget');
        </script>
        <!--- END // CODE TO EMBED --->`;
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant='h6' sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
          {t('webforms:embededCode')}
        </Typography>
        <Divider />
      </Grid>
      <Grid item xs={12} md={4}>
        <Grid item xs={12} mb={2}>
          <FundraiserSettingsAutocomplete
            id='template-picker'
            label={t('fundraiser:selectTemplate')}
            options={themeTemplateArray}
            callback={(x) => setTemplate(x)}
          />
        </Grid>
        <Grid item xs={12} mb={2}>
          <FundraiserSettingsAutocomplete
            id='language-picker'
            label={t('fundraiser:selectLanguage')}
            options={langTemplateArray}
            callback={(x) => setLanguage(x)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('fundraiser:privacyPolicyUrl')}
            placeholder='https://'
            size='small'
            value={policyUrl}
            onChange={(e) => setPolicyUrl(e.target.value)}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={8}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            rows={10}
            multiline
            fullWidth
            label={t('fundraiser:embedCodeLabel')}
            value={getTemplate()}
            onClick={() => {
              navigator.clipboard.writeText(getTemplate());
              setDisplayCopyMessage(true);
              setTimeout(() => {
                setDisplayCopyMessage(false);
              }, 2000);
            }}
            InputProps={{ readOnly: true }}
            sx={{ cursor: 'pointer !important' }}
          />
          <Fade in={displayCopyMessage}>
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                borderRadius: 1,
                bottom: '2px',
                left: '2px',
                right: '2px',
                top: '2px',
                p: 2,
                backgroundColor: 'rgba(241,244,245,0.9)',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <ContentCopyIcon sx={{ fontSize: '3rem', mb: 2 }} />
              <Typography variant='h5'>{t('fundraiser:codeCopiedMessage')}</Typography>
            </Box>
          </Fade>
        </Box>
      </Grid>
      <Grid container item xs={12} flexDirection='row' justifyContent='flex-end' gap={2}>
        <Grid item xs={12} md='auto'>
          <Button
            variant='outlined'
            size='medium'
            fullWidth
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setShowPreviewModal(true)}
            disabled={!getTemplate()}
          >
            {t('fundraiser:preview')}
          </Button>
        </Grid>
        <Grid item xs={12} md='auto'>
          <Button
            variant='outlined'
            size='medium'
            fullWidth
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(getTemplate());
              setDisplayCopyMessage(true);
              setTimeout(() => {
                setDisplayCopyMessage(false);
              }, 2000);
            }}
          >
            {t('fundraiser:copyCode')}
          </Button>
        </Grid>
      </Grid>

      {showPreviewModal && (
        <EmbededCodePreviewModal modalOpen={showPreviewModal} onClose={() => setShowPreviewModal(false)} embeddedCode={getTemplate()} />
      )}
    </>
  );
};
